import { useCallback, useEffect, useRef, useState } from 'react'

import { toast } from 'react-toastify'
import CodeEditor from '../blockly/codeEditor/codeEditor'
import { generateHighlightHtml } from '../blockly/highlight'
import { LuaIcon } from '../icons/luaIcon'
import { useProject } from '../project/hook'
import { useTranslator } from '../translator/hook'
import './button.css'
import { createPortal } from 'react-dom'

export const SmallButtonLua = () => {
  const { t } = useTranslator()
  const { missionManager } = useProject()
  const codeEditor = useRef<HTMLDivElement>(null)
  const [codeEditorVisible, setCodeEditorVisible] = useState(false)

  const updateCode = useCallback(async () => {
    if (codeEditor.current) {
      codeEditor.current.innerHTML =
        missionManager.current?.code ?
          await generateHighlightHtml(missionManager.current.code)
        : ''
    }
  }, [missionManager])

  useEffect(() => {
    missionManager.on('object', updateCode)
    missionManager.on('workspace', updateCode)
    return () => {
      missionManager.off('object', updateCode)
      missionManager.off('workspace', updateCode)
    }
  }, [missionManager, updateCode])

  const handleClickCodeButton = useCallback(() => {
    setCodeEditorVisible((visible) => !visible)
  }, [])

  const closeCodeEditor = useCallback(() => {
    setCodeEditorVisible(false)
  }, [])

  // アプリ全体で文字選択を無効化しているため、コードビューアからのコピーは
  // 選択ではなくこのボタンで行う（生成コードの生文字列を直接コピーする）。
  // 唯一のコピー手段のため、失敗（非 secure context・権限拒否等）は握り潰さず
  // 失敗を通知する。コードが空でも空文字をコピーし、古いクリップボードが残らない
  // ようにする。
  const handleCopyCode = useCallback(async () => {
    const current = missionManager.current
    if (!current) {
      return
    }
    try {
      await navigator.clipboard.writeText(current.code ?? '')
      toast.success(t('code_copied'))
    } catch (error) {
      console.error('コードのコピーに失敗しました', error)
      toast.error(t('code_copy_failed'))
    }
  }, [missionManager, t])

  useEffect(() => {
    if (codeEditorVisible) {
      updateCode()
    }
  }, [codeEditorVisible, updateCode])

  const codePortal = document.getElementById('code-editor-div')

  return (
    <>
      <div className="lua-small-button" onClick={handleClickCodeButton}>
        <LuaIcon style={{ marginRight: '5px', verticalAlign: 'middle' }} />
        {t('show_code')}
      </div>
      {codePortal &&
        createPortal(
          <CodeEditor
            visible={codeEditorVisible}
            editorRef={codeEditor}
            onClose={closeCodeEditor}
            onCopy={handleCopyCode}
            copyLabel={t('code_copy_button')}
          />,
          codePortal,
        )}
    </>
  )
}
